/** @typedef {import('../index.js').BaseDocData} BaseDocData */
/* eslint-disable no-underscore-dangle */
import * as Yup from 'yup';
import { LotKinds, LotStatus, BabyMachines } from '../../constants/index.js';
import YupFirestoreTimestamp from '../yupFirestoreTimestamp.js';

export const lotSchema = Yup.object().shape({
  /**
   * Display code `L-YYYYMMDD-###` — unique by convention, doc ID is an
   * auto-ID. The bag number grows past three digits after 999 bags a day.
   */
  code: Yup.string()
    .matches(/^L-\d{8}-\d{3,}$/, 'Code must look like L-YYYYMMDD-###')
    .required('Code is required'),
  kind: Yup.string().oneOf(Object.values(LotKinds)).required('Kind is required'),
  projectDocId: Yup.string().required('Project is required'),
  pPartDocId: Yup.string().required('Part is required'),
  /** Purchased lots only — supplier is required for them. */
  supplier: Yup.string().trim().nullable().default(null)
    .when('kind', {
      is: LotKinds.PURCHASED,
      // trim runs as a transform first, so whitespace-only fails required.
      then: Yup.string().trim().nullable().required('Supplier is required for purchased lots'),
    }),
  /**
   * PO number, order confirmation, etc. Required on purchased lots (like
   * supplier) — not every order has a PO, but there's always some order
   * reference, and it's what traces a bag back to a specific shipment.
   */
  orderNumber: Yup.string().trim().nullable().default(null)
    .when('kind', {
      is: LotKinds.PURCHASED,
      // trim runs as a transform first, so whitespace-only fails required.
      then: Yup.string().trim().nullable().required('Order number is required for purchased lots'),
    }),
  /** In-house lots only: which molding machine produced the parts. */
  machine: Yup.string().nullable().default(null)
    .when('kind', {
      is: LotKinds.INHOUSE,
      then: Yup.string().oneOf(Object.values(BabyMachines)).nullable()
        .required('Machine is required for in-house lots'),
      // Meaningless on other kinds — force null so it can't be written.
      otherwise: Yup.string().oneOf([null], 'Machine is only valid on in-house lots').nullable(),
    }),
  /**
   * In-house lots only: the molding tool halves on the press. Required when
   * creating a new in-house lot — enforced only under validation context
   * `{ isCreate: true }` (passed by the creation form), because lots created
   * before these fields existed have null and must stay readable.
   */
  aSideTool: Yup.string().trim().nullable().default(null)
    .when('kind', {
      is: LotKinds.INHOUSE,
      then: Yup.string().trim().nullable()
        .when('$isCreate', {
          is: true,
          then: Yup.string().trim().nullable()
            .required('A side tool is required for in-house lots'),
        }),
      otherwise: Yup.string().oneOf([null], 'A side tool is only valid on in-house lots').nullable(),
    }),
  bSideTool: Yup.string().trim().nullable().default(null)
    .when('kind', {
      is: LotKinds.INHOUSE,
      then: Yup.string().trim().nullable()
        .when('$isCreate', {
          is: true,
          then: Yup.string().trim().nullable()
            .required('B side tool is required for in-house lots'),
        }),
      otherwise: Yup.string().oneOf([null], 'B side tool is only valid on in-house lots').nullable(),
    }),
  /** Denormalized from the part for the label (e.g. "Zeonor 1430R"). */
  material: Yup.string().nullable().default(null),
  /** Whole bagged parts — remainingQty stays unconstrained (advisory). */
  initialQty: Yup.number()
    .typeError('Initial quantity must be a number')
    .integer('Initial quantity must be a whole number')
    .positive('Initial quantity must be positive')
    .required('Initial quantity is required'),
  /**
   * Advisory only — quantities drive warnings and the Mark-empty prompt,
   * they never block consumption. The scan graph is the source of truth.
   * Still a whole number of parts (negative allowed: over-consumption).
   */
  remainingQty: Yup.number()
    .integer('Remaining quantity must be a whole number')
    .required('Remaining quantity is required'),
  status: Yup.string().oneOf(Object.values(LotStatus)).required('Status is required'),
  /**
   * Assembled lots only: doc IDs of the source bags scanned during assembly.
   * This is the genealogy graph — query downstream with `array-contains`.
   */
  sourceLotIds: Yup.array().of(Yup.string()).default([]),
  statusChangedAt: YupFirestoreTimestamp.nullable().default(null),
  _createdBy: Yup.object().shape({
    uid: Yup.string().required('_createdBy.uid is required'),
    email: Yup.string().email().required('_createdBy.email is required'),
    timestamp: YupFirestoreTimestamp.nullable(),
  }).required('_createdBy is required'),
});

/**
 * @typedef {Yup.InferType<typeof lotSchema>} LotSchema
 * @typedef {BaseDocData & LotSchema} LotDocData
 */

export default class Lot {
  constructor({
    id, code, kind, projectDocId, pPartDocId,
    supplier, orderNumber, machine, aSideTool, bSideTool,
    material, initialQty, remainingQty,
    status, sourceLotIds, statusChangedAt, _createdBy,
  }) {
    const validatedData = lotSchema.validateSync({
      code,
      kind,
      projectDocId,
      pPartDocId,
      supplier,
      orderNumber,
      machine,
      aSideTool,
      bSideTool,
      material,
      initialQty,
      remainingQty,
      status,
      sourceLotIds,
      statusChangedAt,
      _createdBy,
    });

    this.id = id || null;
    this.code = validatedData.code;
    this.kind = validatedData.kind;
    this.projectDocId = validatedData.projectDocId;
    this.pPartDocId = validatedData.pPartDocId;
    this.supplier = validatedData.supplier ?? null;
    this.orderNumber = validatedData.orderNumber ?? null;
    this.machine = validatedData.machine ?? null;
    this.aSideTool = validatedData.aSideTool ?? null;
    this.bSideTool = validatedData.bSideTool ?? null;
    this.material = validatedData.material ?? null;
    this.initialQty = validatedData.initialQty;
    this.remainingQty = validatedData.remainingQty;
    this.status = validatedData.status;
    this.sourceLotIds = validatedData.sourceLotIds || [];
    this.statusChangedAt = validatedData.statusChangedAt ?? null;
    this._createdBy = validatedData._createdBy;
  }

  toFirestore({ serverTimestamp }) {
    return {
      code: this.code,
      kind: this.kind,
      projectDocId: this.projectDocId,
      pPartDocId: this.pPartDocId,
      supplier: this.supplier,
      orderNumber: this.orderNumber,
      machine: this.machine,
      aSideTool: this.aSideTool,
      bSideTool: this.bSideTool,
      material: this.material,
      initialQty: this.initialQty,
      remainingQty: this.remainingQty,
      status: this.status,
      sourceLotIds: this.sourceLotIds,
      statusChangedAt: this.statusChangedAt || serverTimestamp(),
      _createdBy: {
        uid: this._createdBy.uid,
        email: this._createdBy.email,
        timestamp: this._createdBy.timestamp || serverTimestamp(),
      },
    };
  }

  static fromFirestore(docData) {
    return new Lot(docData);
  }
}
